/**
 * Narrowing the message list: unread, flagged, attachments, a date range.
 *
 * # Why the server does all of it
 *
 * Every condition here is one the daemon already answers. `Email/query` takes
 * `notKeyword`, `hasKeyword`, `hasAttachment`, `before` and `after`, and
 * `hm-store` turns each into an indexed predicate rather than a scan -- against
 * the real 14,000-message account each of these returns in single-digit
 * milliseconds. Filtering in the browser instead would have been quicker to
 * write and wrong in a way that is hard to see: the list is paged, so a local
 * predicate applied to the forty rows in hand would show "3 unread" when the
 * account holds two hundred, and the number would climb as the reader scrolled.
 * A filter has to be part of the question, not part of the answer.
 *
 * # Why a filter is not a search
 *
 * They compose rather than replace each other -- `filterFor` in `jmap.ts` ANDs
 * the scope, the text and these together, which is one query and not three
 * intersected by hand. But they are separate controls because they fail
 * differently: a search that matches nothing is usually a typo, and a filter
 * that matches nothing is usually correct and worth saying out loud.
 *
 * # The dates are the reader's dates
 *
 * `since` and `until` are `YYYY-MM-DD` as an `<input type="date">` produces
 * them, and they become UTC instants at *local* midnight, because someone
 * asking for mail since the 3rd means their own 3rd. `until` is inclusive here
 * and exclusive on the wire -- JMAP's `before` is `receivedAt <` -- so it is
 * sent as midnight at the start of the following day. Getting that off by one
 * would silently drop everything that arrived on the last day of the range,
 * which is the day someone picking a range cares most about.
 *
 * # Nothing is persisted
 *
 * The state lives in a signal and dies with the page. A filter that survives a
 * reload is a filter nobody remembers turning on, met on a fresh tab days later
 * with no interaction nearby to explain why the inbox looks empty -- which is
 * the exact failure this feature has to avoid. It does survive changing folder
 * and changing the search text, because "show me what I have not read" is a way
 * of reading rather than a property of one mailbox, and a mode that evaporated
 * on every click in the sidebar could not be used for the triage pass it exists
 * for. The cost of carrying it is paid by making it loud: see `FilterBar`.
 */

import { computed, signal } from "@preact/signals";

import type { Email } from "./types";

export interface Filters {
  unread: boolean;
  flagged: boolean;
  attachment: boolean;
  /** `YYYY-MM-DD`, inclusive. Empty means no lower bound. */
  since: string;
  /** `YYYY-MM-DD`, inclusive. Empty means no upper bound. */
  until: string;
}

export const NONE: Filters = {
  unread: false,
  flagged: false,
  attachment: false,
  since: "",
  until: "",
};

export const filters = signal<Filters>(NONE);

/** The toggles, as a list, so the bar and the keyboard agree on what exists. */
export type Toggle = "unread" | "flagged" | "attachment";

/**
 * `hint` is what a screen reader gets, because the visible word is one item in a
 * list and only makes sense read against the others. "Attachment, pressed" does
 * not say what pressing it did.
 */
export const TOGGLES: { key: Toggle; label: string; hint: string }[] = [
  { key: "unread", label: "Unread", hint: "Show only unread messages" },
  { key: "flagged", label: "Flagged", hint: "Show only flagged messages" },
  { key: "attachment", label: "Attachment", hint: "Show only messages with an attachment" },
];

/**
 * How many conditions are on. The date range counts once however many of its
 * two ends are set: it is one thing the reader turned on and one thing they
 * would expect to turn off.
 */
export const activeCount = computed(() => {
  const f = filters.value;
  let n = 0;
  for (const { key } of TOGGLES) if (f[key]) n += 1;
  if (f.since || f.until) n += 1;
  return n;
});

export const anyActive = computed(() => activeCount.value > 0);

export function toggle(key: Toggle): void {
  filters.value = { ...filters.value, [key]: !filters.value[key] };
}

export function setDates(since: string, until: string): void {
  filters.value = { ...filters.value, since, until };
}

export function clear(): void {
  if (!anyActive.peek()) return;
  filters.value = NONE;
}

// ---- the wire form -----------------------------------------------------------

/** The JMAP `FilterCondition` fields these settings imply, or nothing. */
export function conditions(f: Filters): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  // `notKeyword` rather than a `NOT` operator around `hasKeyword`: the store
  // compiles it to a single `NOT EXISTS` on the keyword join, and it can sit in
  // the same condition object as everything else instead of forcing the filter
  // into a tree.
  if (f.unread) out["notKeyword"] = "$seen";
  if (f.flagged) out["hasKeyword"] = "$flagged";
  if (f.attachment) out["hasAttachment"] = true;
  const after = dayStart(f.since);
  if (after) out["after"] = after;
  const before = dayStart(f.until, 1);
  if (before) out["before"] = before;
  return out;
}

/** Local midnight on `day`, plus `offset` days, as a JMAP UTCDate. */
function dayStart(day: string, offset = 0): string | undefined {
  if (!day) return undefined;
  const parts = day.split("-").map(Number);
  const [y, m, d] = parts;
  if (parts.length !== 3 || y === undefined || m === undefined || d === undefined) return undefined;
  const at = new Date(y, m - 1, d + offset);
  if (Number.isNaN(at.getTime())) return undefined;
  return at.toISOString();
}

// ---- the local form ----------------------------------------------------------

/**
 * Whether a message still satisfies the filters, decided here rather than by
 * asking the server again.
 *
 * Used for one thing only: noticing that something the reader *just did* --
 * opening an unread message, unflagging a flagged one -- has taken a row out of
 * its own list. Never used to decide what to show, because a local answer over
 * a paged list is not the same question as the server's. See `store.reprieved`.
 */
export function matches(email: Email, f: Filters): boolean {
  if (f.unread && email.keywords["$seen"]) return false;
  if (f.flagged && !email.keywords["$flagged"]) return false;
  if (f.attachment && !email.hasAttachment) return false;
  const at = Date.parse(email.receivedAt);
  const after = dayStart(f.since);
  if (after && at < Date.parse(after)) return false;
  const before = dayStart(f.until, 1);
  if (before && at >= Date.parse(before)) return false;
  return true;
}

// ---- saying so ---------------------------------------------------------------

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** `2026-03-04` as `4 Mar`, or with the year when it is not this one. */
export function dayLabel(day: string): string {
  const parts = day.split("-").map(Number);
  const [y, m, d] = parts;
  if (y === undefined || m === undefined || d === undefined) return day;
  const month = MONTHS[m - 1] ?? String(m);
  const year = y === new Date().getFullYear() ? "" : ` ${y}`;
  return `${d} ${month}${year}`;
}

/** The range in a few words, for the date group's heading in the filter panel. */
export function dateLabel(f: Filters): string {
  if (f.since && f.until) return `${dayLabel(f.since)} – ${dayLabel(f.until)}`;
  if (f.since) return `Since ${dayLabel(f.since)}`;
  if (f.until) return `Until ${dayLabel(f.until)}`;
  return "Date";
}

/**
 * The active filters as English, split around the word "messages".
 *
 * Two halves rather than one string because some of these are adjectives and
 * some are prepositional phrases, and a single list produces "No with an
 * attachment messages in Inbox". Both the sentence over the list and the empty
 * state need to read properly, and the empty state needs it most: that is the
 * moment someone is deciding between "I have read everything" and "this app
 * has lost my mail", and a sentence that reads like a debug dump does not help
 * them decide.
 */
export function phrase(f: Filters): { pre: string; post: string } {
  const pre: string[] = [];
  if (f.unread) pre.push("unread");
  if (f.flagged) pre.push("flagged");

  const post: string[] = [];
  if (f.attachment) post.push("with an attachment");
  if (f.since && f.until) post.push(`from ${dayLabel(f.since)} to ${dayLabel(f.until)}`);
  else if (f.since) post.push(`since ${dayLabel(f.since)}`);
  else if (f.until) post.push(`up to ${dayLabel(f.until)}`);

  return { pre: list(pre), post: list(post) };
}

function list(parts: string[]): string {
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0] as string;
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

/** "unread messages with an attachment", the noun phrase both callers need. */
export function messagesPhrase(f: Filters): string {
  const { pre, post } = phrase(f);
  return `${pre ? `${pre} ` : ""}messages${post ? ` ${post}` : ""}`;
}
